import { AuthType } from '@shared/utils/AuthUtils';
import { getStoreRef } from './storeRef';
import { getActiveGateway } from '@shared/auth/AuthGatewayProvider';
import { NoMsalAccountError, SessionTokens } from '@shared/auth/types';

export interface AuthHeaders {
  Authorization?: string;
  AccessToken?: string;
  'x-auth-type'?: string;
  idToken?: string;
}

function getAuthType(): AuthType | null {
  return getStoreRef()?.getState().auth.authType ?? null;
}

function getTeamsSsoMsalHeaders(): AuthHeaders {
  // No MSAL account — fall back to localStorage (Teams SSO context).
  // Teams SSO tokens bypass MSAL's cache and are stored directly in localStorage.
  const accessToken = localStorage.getItem('accessToken');
  const idToken = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  if (!accessToken) return {};

  return {
    'Authorization': 'Bearer ' + idToken,
    'AccessToken': accessToken,
    'x-auth-type': AuthType.Microsoft,
  };
}

function getGoogleHeaders(): AuthHeaders {
  const idToken = localStorage.getItem('idToken');
  const accessToken = localStorage.getItem('accessToken');
  if (!idToken) return {};

  return {
    'idToken': idToken,
    'AccessToken': accessToken!,
    'Authorization': 'JWT ' + accessToken,
    'x-auth-type': AuthType.Google,
  };
}

function getStandardHeaders(): AuthHeaders {
  const headers: AuthHeaders = {};
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) headers.AccessToken = accessToken;

  const idToken = localStorage.getItem('idToken');
  if (idToken) {
    headers.idToken = idToken;
    headers.Authorization = 'JWT ' + idToken;
    headers['x-auth-type'] = AuthType.Standard;
  }

  return headers;
}

function sessionTokensToHeaders(tokens: SessionTokens): AuthHeaders {
  return {
    'Authorization': 'Bearer ' + tokens.idToken,
    'AccessToken': tokens.accessToken,
    'x-auth-type': AuthType.Microsoft,
  };
}

export async function getAuthHeaders(): Promise<AuthHeaders> {
  const authType = getAuthType();

  if (authType === AuthType.Microsoft) {
    const gateway = getActiveGateway();
    if (gateway) {
      try {
        const tokens = await gateway.acquireSession({ source: 'auto' });
        return sessionTokensToHeaders(tokens);
      } catch (err) {
        // No MSAL account yet (pre-auth pages like /invite, or signed-out state).
        // Fall back to any Teams SSO / localStorage tokens, otherwise send
        // anonymous headers so public endpoints work and protected ones get a
        // clean 401 → login redirect, instead of surfacing a raw internal error.
        if (err instanceof NoMsalAccountError) {
          return getTeamsSsoMsalHeaders();
        }
        throw err;
      }
    }
    return getTeamsSsoMsalHeaders();
  }

  if (authType === AuthType.Google) {
    return getGoogleHeaders();
  }

  return getStandardHeaders();
}

export async function refreshMsalToken(): Promise<AuthHeaders> {
  const gateway = getActiveGateway();
  if (!gateway) {
    throw new Error('No active auth gateway');
  }
  const tokens = await gateway.acquireSession({ source: 'agent-401-retry', forceRefresh: true });
  return sessionTokensToHeaders(tokens);
}
